/**
 * @author Muhammad Fahad
 * @datetime April, 20th 2022
 * @description: This is floor management service that is responsible to call
 *               floor related APIs and return data
 */
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ERROR_CODES } from 'src/app/shared/constants/error-codes';
import { LANGUAGE_CONSTANTS as LANG_CONST } from 'src/app/shared/constants/language-constants';
import {
  Floor,
  IFloor,
  IFloorExtend,
  Units,
} from './floor-management-interface';
import { StateManagementService } from '../shared/services/state-management.service';
import { Utility } from '../shared/utils/utility.service';
import { AccessPoint } from '../building-managment/building-unit-details/activity/access-point-interfaces';
import { FloorUnit } from './floor-item/floor-item-interfaces';
import { CONSTANTS } from '../shared/constants/constants';
import { checkGenericErrors } from '../shared/errors/generic-error-response';
import {
  DuplicateDataError,
  NotFoundError,
  OperationFailedError,
} from '../shared/errors/errors';
import { IDevice } from '../building-managment/building-unit-details/add-lock/lock-management-interfaces';

import { IFilterFloor } from '../shared/state-management/state-interfaces';
import { Activities } from '../shared-layout/Components/activity/activity.interface';
import {
  accessPointStatusResponse,
  activationStateResponse,
  deviceStateResponse,
  deviceStatusResponse,
  unitStatusResponse,
} from '../shared/constants/response-mapping';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class FloorManagementService {
  public buildingsView = false;
  private url = environment.graphqlUrl;
  readonly LANG_CONST = LANG_CONST;
  // time to live (cache object) is 60 seconds
  private ttl = 60000;
  private initialTime = Date.now();
  private buildingId = undefined;

  public triggerRerender = new BehaviorSubject<boolean>(false);

  /**
   * @description injecting services to the constructor
   * @param { HttpClient } http
   */
  constructor(
    private http: HttpClient,
    private stateManagementService: StateManagementService,
    private translate: TranslateService
  ) {}

  /**
   * @description this method set the value of triggerRerender which runs the subscription to re-render UI of unit and accesspoint list
   * @param { boolean } value toggle boolean to re-render user interface when user click on unit or accesspoint
   */
  setTriggerRerender(value: boolean) {
    this.triggerRerender.next(value);
  }

  /**
   * @description this method calls getFloorsByBuilding API to fetch list of floors within a building
   * @param { any } siteId
   * @param { any } buildingId
   * @returns
   */
  getAllFloorsByBuildingId(
    siteId: any,
    buildingId: any,
    loader?: boolean
  ): Observable<Floor> {
    const localFloors = this.stateManagementService.getFloors();
    if (
      !(Utility.checkValidValue(siteId) && Utility.checkValidValue(buildingId))
    ) {
      if (window.__dev__)
        console.log(
          'getAllFloorsByBuildingId Id can not be null SiteId: ',
          siteId,
          ' BuildingId',
          buildingId
        );
      return new Observable((observer) => {
        observer.error(
          this.translate.instant('SITE_OR_BUILDING_ID_CAN_NOT_BE_NULL')
        );
      });
    } else if (
      localFloors &&
      localFloors.length > 0 &&
      this.buildingId === buildingId &&
      Utility.checkTTLExpiry(this.ttl, this.initialTime)
    ) {
      return new Observable((observer) => {
        observer.next(localFloors);
      });
    }
    return this.http
      .post<any>(
        `${this.url}`,
        {
          query:
            'query GetFloorsByBuilding($siteId: String!, $buildingId: String!) {  getFloorsByBuilding(siteId: $siteId, buildingId: $buildingId) { floorId floorName wingName accessPointsCount unitsCount }}',
          variables: {
            siteId: siteId,
            buildingId: buildingId,
          },
          operationName: 'GetFloorsByBuilding',
        },
        {
          headers: Utility.loaderHeader(loader),
        }
      )
      .pipe(
        map((res: any) => {
          if (res?.data && res?.data?.getFloorsByBuilding?.length > 0) {
            this.initialTime = Date.now();
            this.buildingId = buildingId;
            const floors: Floor = res.data.getFloorsByBuilding;
            this.stateManagementService.updateFloors(floors);
            return floors;
          } else if (res?.errors?.length > 0) {
            this.stateManagementService.updateFloors([]);
            throw new Error(res?.errors[0].errorType);
          }
        }),
        catchError((err: any) => {
          switch (err.message) {
            case ERROR_CODES.FLOOR_NOT_FOUND:
              throw new NotFoundError(
                this.translate.instant('NO_FLOORS_IN_THIS_BUILDING')
              );
            case ERROR_CODES.FAILED:
              throw new OperationFailedError(
                this.translate.instant('FAILED_TO_LOAD_FLOORS_OF_THIS_BUILDING')
              );
            default:
              throw new Error(
                this.translate.instant('REQUEST_TO_GET_FLOORS_FAILED')
              );
          }
        })
      );
  }

  /**
   * @description this method add new floor to the building by calling addFloor API
   * @param { string } siteId
   * @param { string } buildingId
   * @param { string } floorName
   * @param { number } unitsCount
   * @param { string } wingName
   * @param { number } accessPointsCount [ Optional ]
   * @returns { Observable<IFloor | undefined> }
   */
  addFloor(
    siteId: string,
    buildingId: string,
    floorName: string,
    unitsCount: number,
    wingName?: string,
    accessPointsCount?: number
  ): Observable<IFloor | undefined> {
    const variables: any = {
      siteId: siteId,
      buildingId: buildingId,
      floorName: floorName,
      unitsCount: unitsCount,
      wingName: wingName,
    };
    // Only include accessPointsCount in variables if it's provided
    if (accessPointsCount) {
      variables.accessPointsCount = accessPointsCount;
    }
    return this.http
      .post<any>(`${this.url}`, {
        query:
          'mutation ($siteId: String!, $accessPointsCount: Int, $buildingId: String!, $floorName: String!, $unitsCount: Int, $wingName: String) {   addFloor(accessPointsCount: $accessPointsCount, buildingId: $buildingId, floorName: $floorName, unitsCount: $unitsCount, wingName: $wingName, siteId: $siteId) {     floorId   } }',
        variables: variables,
      })
      .pipe(
        map((res: any) => {
          if (res?.data && res?.data?.addFloor) {
            const floor: IFloorExtend = res.data.addFloor;
            floor.floorName = floorName;
            floor.wingName = wingName;
            floor.accessPointsCount = accessPointsCount || 0;
            floor.unitsCount = unitsCount;
            const floors = this.stateManagementService.getFloors();
            floors?.push(floor);
            this.stateManagementService.updateFloors(floors);
            return floor;
          } else if (res?.errors?.length > 0) {
            throw new Error(res?.errors[0]?.errorType);
          }
        }),
        catchError((err: any) => {
          switch (err.message) {
            case ERROR_CODES.FLOOR_NOT_FOUND:
              throw new NotFoundError(
                this.translate.instant('NO_FLOORS_IN_THIS_BUILDING')
              );
            case ERROR_CODES.FLOOR_ALREADY_EXISTS:
              throw new DuplicateDataError(
                this.translate.instant('FLOOR_AND_WING_NAME_ALREADY_EXISTS')
              );
            case ERROR_CODES.FLOOR_LIMIT_EXCEEDEDS:
              throw new OperationFailedError(
                this.translate.instant('FAILED_FLOOR_LIMIT_EXCEEDEDS')
              );
            case ERROR_CODES.UNIT_ACCESS_POINT_LIMIT_EXCEEDED:
              throw new OperationFailedError(
                this.translate.instant(
                  'REQUEST_TO_ADD_FLOOR_FAILED_LIMIT_EXCEED'
                )
              );
            default:
              throw new Error(
                this.translate.instant('REQUEST_TO_ADD_FLOOR_FAILED')
              );
          }
        })
      );
  }

  /**
   * @description this method update existing floor by calling updateFloor API
   * @param { string } siteId
   * @param { string } buildingId
   * @param { string } floorId
   * @param { string } floorName
   * @param { number } unitsCount
   * @param { string } wingName
   * @param { number } accessPointsCount
   * @returns { Observable<IFloor | undefined> }
   */
  updateFloor(floor: any): Observable<IFloor | undefined> {
    // Only include accessPointsCount in variables if it's provided
    if (floor?.accessPointsCount) {
      floor.accessPointsCount = floor.accessPointsCount;
    }
    return this.http
      .post<any>(`${this.url}`, {
        query:
          'mutation ($siteId:String!, $buildingId:String!, $floorId:String!, $wingName:String, $accessPointsCount:Int, $unitsCount:Int, $floorName: String) { updateFloor(siteId: $siteId, buildingId: $buildingId, floorId:$floorId, wingName:$wingName, accessPointsCount:$accessPointsCount, unitsCount:$unitsCount, floorName:$floorName) { floorId }   }',
        variables: floor,
      })
      .pipe(
        map((res: any) => {
          if (res?.data && res?.data?.updateFloor) {
            const updatedFloor: IFloor = {
              floorId: res.data.updateFloor?.floorId,
              floorName: floor?.floorName,
              wingName: floor?.wingName,
              accessPointsCount: floor?.accessPointsCount || 0,
              unitsCount: floor?.unitsCount,
            };
            const floors = this.stateManagementService.getFloors();
            floors?.forEach((currentFloor) => {
              if (currentFloor?.floorId === updatedFloor?.floorId) {
                currentFloor.floorName = updatedFloor?.floorName;
                currentFloor.wingName = updatedFloor?.wingName;
                currentFloor.accessPointsCount =
                  updatedFloor?.accessPointsCount;
                currentFloor.unitsCount = updatedFloor?.unitsCount;
              }
            });
            this.stateManagementService.updateFloors(floors);
            this.stateManagementService.updateUnits(floor?.floorId, undefined);
            return updatedFloor;
          } else if (res?.errors?.length > 0) {
            throw new Error(res?.errors[0]?.errorType);
          }
        }),
        catchError((err: any) => {
          switch (err.message) {
            case ERROR_CODES.FLOOR_NOT_FOUND:
              throw new NotFoundError(
                this.translate.instant('NO_FLOORS_IN_THIS_BUILDING')
              );
            case ERROR_CODES.FLOOR_ALREADY_EXISTS:
              throw new DuplicateDataError(
                this.translate.instant('FLOOR_AND_WING_NAME_ALREADY_EXISTS')
              );
            case ERROR_CODES.FAILED:
              throw new OperationFailedError(
                this.translate.instant('REQUEST_TO_UPDATE_FLOOR_FAILED')
              );
            case ERROR_CODES.UNIT_ACCESS_POINT_LIMIT_EXCEEDED:
              throw new OperationFailedError(
                this.translate.instant(
                  'REQUEST_TO_ADD_FLOOR_FAILED_LIMIT_EXCEED'
                )
              );
            default:
              throw new Error(
                this.translate.instant('REQUEST_TO_UPDATE_FLOOR_FAILED')
              );
          }
        })
      );
  }

  /**
   * @description this method deletes floor from building bby calling deleteFloor API
   * @param { string } siteId
   * @param { string } buildingId
   * @param { string } floorId
   * @returns { Observable<any> }
   */
  deleteFloor(floorId: any, siteId: any, buildingId: any): Observable<any> {
    return this.http
      .post<any>(`${this.url}`, {
        query:
          'mutation($floorId:String!,$siteId:String!, $buildingId:String!) { deleteFloor( siteId: $siteId, buildingId: $buildingId,floorId: $floorId) { floorId  }}',
        variables: {
          floorId: floorId,
          siteId: siteId,
          buildingId: buildingId,
        },
      })
      .pipe(
        map((res: any) => {
          if (res?.data?.deleteFloor?.floorId) {
            const floors = this.stateManagementService.getFloors();
            const updatedFloors = floors?.filter(
              (currentFloor) =>
                currentFloor?.floorId !== res?.data?.deleteFloor?.floorId
            );
            this.stateManagementService.updateFloors(updatedFloors);
            return res?.data?.deleteFloor;
          } else if (res.data?.deleteFloor.hasOwnProperty('error')) {
            throw new Error(res.data?.deleteFloor?.error[0]?.errorType);
          }
        }),
        catchError((err: any) => {
          switch (err.message) {
            case ERROR_CODES.FLOOR_NOT_FOUND:
              throw new NotFoundError(
                this.translate.instant('NO_FLOORS_IN_THIS_BUILDING')
              );
            case ERROR_CODES.FAILED:
              throw new OperationFailedError(
                this.translate.instant('REQUEST_TO_DELETE_FLOOR_FAILED')
              );
            default:
              throw new Error(
                this.translate.instant('REQUEST_TO_DELETE_FLOOR_FAILED')
              );
          }
        })
      );
  }

  /**
   * @description this method returns all units within the floor
   * @param { any } siteId
   * @param { any } buildingId
   * @param { any } floorId
   * @returns { Observable<any> }
   */
  getUnitsByFloor(siteId: any, buildingId: any, floorId: any): Observable<any> {
    const localUnits = this.stateManagementService.getUnits(floorId);
    if (localUnits && Utility.checkTTLExpiry(this.ttl, this.initialTime)) {
      return new Observable((observer) => {
        observer.next(localUnits);
      });
    }
    return this.http
      .post<any>(`${this.url}`, {
        query: `query getUnitsAndAccessPointsByFloors ($siteId: String!, $buildingId: String!, $floorId: String!) { getUnitsAndAccessPointsByFloor(buildingId: $buildingId, floorId: $floorId, siteId: $siteId) { __typename ... on Unit { floorName deviceCount unitId unitName unitStatus wingName } ... on AccessPoint { accessPointName accessPointStatus accessPointId floorId }}}`,
        variables: {
          siteId: siteId,
          buildingId: buildingId,
          floorId: floorId,
        },
      })
      .pipe(
        map((res: any) => {
          if (res?.data?.getUnitsAndAccessPointsByFloor) {
            const data = res?.data?.getUnitsAndAccessPointsByFloor;
            const units = data?.filter(
              (record: any) => record?.__typename === CONSTANTS.UNITS
            );
            const accessPoints = data?.filter(
              (record: any) => record?.__typename === CONSTANTS.ACCESS_POINT
            );
            this.stateManagementService.updateUnits(floorId, units);
            return [units, accessPoints];
          } else if (res.data?.deleteFloor.hasOwnProperty('error')) {
            throw new Error(res.data?.deleteFloor?.error[0]?.errorType);
          }
        }),
        catchError((err: any) => {
          switch (err.message) {
            case ERROR_CODES.UNIT_NOT_FOUND:
              throw new NotFoundError(
                this.translate.instant('UNIT_NOT_FOUND_IN_THIS_FLOOR')
              );
            default:
              throw new Error(
                this.translate.instant('UNIT_NOT_FOUND_IN_THIS_FLOOR')
              );
          }
        })
      );
  }

  /**
   * @description this method returns all units within the floor
   * @param { any } siteId
   * @param { any } buildingId
   * @param { any } floorId
   * @returns { Observable<any> }
   */
  getUnitsAndAccessPointsByFloor(
    siteId: any,
    buildingId: any,
    floorId: any,
    loader?: boolean
  ): Observable<FloorUnit> {
    const commonAccessControl: boolean =
      this.stateManagementService.getSharedData()?.selectedSite
        ?.commonAccessControl;

    return this.http
      .post<any>(
        `${this.url}`,
        {
          query: `query getUnitsAndAccessPointsByFloor ($buildingId: String!, $floorId: String!, $siteId: String!) { getUnitsAndAccessPointsByFloor (buildingId: $buildingId, floorId: $floorId, siteId: $siteId) { 
          ... on Unit {
          floorName
          deviceCount
          unitId
          unitName
          unitStatus
          wingName
          isLockBatteryLow
          unitPrefix
          firmwareUpdateAvailable
          }
          ... on AccessPoint {
          accessPointName
          accessPointStatus
          accessPointId
          floorId
          }
         } 
        }`,
          variables: {
            buildingId: buildingId,
            floorId: floorId,
            siteId: siteId,
          },
          operationName: 'getUnitsAndAccessPointsByFloor',
        },
        {
          headers: Utility.loaderHeader(loader),
        }
      )
      .pipe(
        map((res: any) => {
          if (res?.data?.getUnitsAndAccessPointsByFloor) {
            let unitsAndAccessPoints =
              res?.data?.getUnitsAndAccessPointsByFloor;

            if (!commonAccessControl) {
              unitsAndAccessPoints = unitsAndAccessPoints.filter(
                (ap: any) => !ap.hasOwnProperty('accessPointId')
              );
            }

            const updatedUnits = unitsAndAccessPoints?.map((item: any) => {
              if (item) {
                const isAccessPoint = 'accessPointName' in item;
                const statusKey = isAccessPoint
                  ? 'accessPointStatus'
                  : 'unitStatus';

                return {
                  ...item,
                  [statusKey]: isAccessPoint
                    ? this.translate.instant(
                        accessPointStatusResponse[item.accessPointStatus]
                      )
                    : this.translate.instant(
                        unitStatusResponse[item.unitStatus]
                      ),
                };
              }
              return item; // Handle the case where item is null
            });

            this.stateManagementService.updateUnits(floorId, updatedUnits);
            return updatedUnits;
          } else if (res.hasOwnProperty('error')) {
            throw new Error(ERROR_CODES.UNIT_NOT_FOUND);
          }
        }),
        catchError((err: any) => {
          switch (err.message) {
            case ERROR_CODES.UNIT_NOT_FOUND:
              throw new NotFoundError(
                this.translate.instant('UNIT_NOT_FOUND_IN_THIS_FLOOR')
              );
            default:
              throw new Error(
                this.translate.instant('UNIT_NOT_FOUND_IN_THIS_FLOOR')
              );
          }
        })
      );
  }
  /**
   * @description this method returns all units within the floor
   * @param { any } siteId
   * @param { any } buildingId
   * @param { any } floorId
   * @returns { Observable<any> }
   */
  getUnitsByFloorId(
    siteId: String | null | undefined,
    buildingId: string,
    floorId: string
  ): Observable<any> {
    const localUnits = this.stateManagementService.getUnits(floorId);
    return this.http
      .post<any>(`${this.url}`, {
        query:
          'query getUnitsByFloor ($siteId: String!, $buildingId: String!, $floorId: String!) { getUnitsByFloor (siteId: $siteId, buildingId: $buildingId, floorId: $floorId) { unitId, unitName, unitStatus } }',
        variables: {
          siteId: siteId,
          buildingId: buildingId,
          floorId: floorId,
        },
        operationName: 'getUnitsByFloor',
      })
      .pipe(
        map((res: any) => {
          if (res?.data?.getUnitsByFloor) {
            const units = res?.data?.getUnitsByFloor;
            const filteredUnits = units.filter((element: any) => element);
            this.stateManagementService.updateUnits(floorId, filteredUnits);
            return filteredUnits;
          } else if (res.hasOwnProperty('error')) {
            throw new Error(ERROR_CODES.UNIT_NOT_FOUND);
          }
        }),
        catchError((err: any) => {
          switch (err.message) {
            case ERROR_CODES.UNIT_NOT_FOUND:
              throw new NotFoundError(
                this.translate.instant('UNIT_NOT_FOUND_IN_THIS_FLOOR')
              );
            default:
              throw new Error(
                this.translate.instant('UNIT_NOT_FOUND_IN_THIS_FLOOR')
              );
          }
        })
      );
  }

  /**
   * @description This method hit babckend API deleteUnit
   * @input {Any}
   * @param { siteId, buildingId , floorId , unitId }
   * @returns observable
   */

  deleteUnit(
    siteId: any,
    buildingId: any,
    floorId: any,
    unitId: any
  ): Observable<any> {
    return this.http
      .post<any>(`${this.url}`, {
        query:
          'mutation ($siteId: String!, $buildingId: String!, $floorId: String! , $unitId: String!) { deleteUnit (siteId: $siteId, buildingId: $buildingId, floorId: $floorId, unitId: $unitId) {  unitId } }',
        variables: {
          siteId: siteId,
          buildingId: buildingId,
          floorId: floorId,
          unitId: unitId,
        },
      })
      .pipe(
        map((res) => {
          if (res?.data?.deleteUnit?.unitId) {
            const units: Units = this.stateManagementService.getUnits(floorId);
            const updatedUnits: Units = units?.filter(
              (currentUnit) =>
                currentUnit?.unitId !== res?.data?.deleteUnit?.unitId
            );
            this.stateManagementService.updateUnits(floorId, updatedUnits);
            const floors = this.stateManagementService.getFloors();
            floors?.forEach((currentFloor) => {
              if (currentFloor?.floorId === floorId) {
                currentFloor.unitsCount--;
              }
            });
            this.stateManagementService.updateFloors(floors);
            return res?.data?.deleteUnit;
          } else if (res.data?.deleteUnit.hasOwnProperty('error')) {
            throw new Error(ERROR_CODES.FAILED);
          }
        }),
        catchError((err: any) => {
          switch (err.message) {
            case ERROR_CODES.FAILED:
              throw new OperationFailedError(
                this.translate.instant('REQUEST_TO_DELETE_UNIT_FAILED')
              );
            default:
              throw new Error(
                this.translate.instant('REQUEST_TO_DELETE_UNIT_FAILED')
              );
          }
        })
      );
  }

  /**
   * @description This method hit babckend API deleteAccessPoint
   * @input {Any}
   * @param { siteId, buildingId , floorId , unitId }
   * @returns observable
   */

  deleteAccessPoint(
    siteId: any,
    buildingId: any,
    floorId: any,
    accessPointId: any
  ): Observable<any> {
    return this.http
      .post<any>(`${this.url}`, {
        query:
          'mutation ($accessPointId: String!) { deleteAccessPoint (accessPointId: $accessPointId) {  accessPointId } }',
        variables: {
          accessPointId: accessPointId,
        },
      })
      .pipe(
        map((res) => {
          if (res?.data?.deleteAccessPoint?.accessPointId) {
            const accesspoints: any =
              this.stateManagementService.getUnits(floorId);
            const updatedAP: any = accesspoints?.filter(
              (currentAP: any) =>
                currentAP?.accessPointId !==
                res?.data?.deleteAccessPoint?.accessPointId
            );
            this.stateManagementService.updateUnits(floorId, updatedAP);
            const floors = this.stateManagementService.getFloors();
            floors?.forEach((currentFloor) => {
              if (currentFloor?.floorId === floorId) {
                currentFloor.accessPointsCount--;
              }
            });
            this.stateManagementService.updateFloors(floors);
            return res?.data?.deleteAccessPoint?.accessPointId;
          } else if (
            res.data?.deleteAccessPoint.hasOwnProperty('error') ||
            res.hasOwnProperty('errors')
          ) {
            throw new Error(ERROR_CODES.FAILED);
          }
        }),
        catchError((err: any) => {
          if (window.__dev__) console.log(err);
          checkGenericErrors(err.message, this.translate);
          switch (err.message) {
            case ERROR_CODES.FAILED:
              throw new OperationFailedError(
                this.translate.instant('REQUEST_TO_DELETE_ACCESS_POINT_FAILED')
              );
            default:
              throw new Error(
                this.translate.instant('REQUEST_TO_DELETE_ACCESS_POINT_FAILED')
              );
          }
        })
      );
  }

  /**
   * @description this method calls backend API of update unit
   * @param { string } siteId
   * @param { string } buildingId
   * @param { string } floorId
   * @param { string } unitId
   * @param { string } unitName
   * @param { string } unitStatus
   * @returns { Observable }
   */
  updateUnit(
    siteId: string,
    buildingId: string,
    floorId: string,
    unitId: string,
    unitName: string,
    unitStatus: string
  ): Observable<any> {
    return this.http
      .post<any>(`${this.url}`, {
        query:
          'mutation ($siteId:String!, $buildingId:String!,$floorId:String!, $unitId:String!, $unitName:String!, $unitStatus:Int) { updateUnit(siteId: $siteId, buildingId: $buildingId, floorId:$floorId, unitId:$unitId, unitName:$unitName , unitStatus:$unitStatus ) { unitId }}',
        variables: {
          unitName: unitName,
          unitStatus: unitStatus,
          unitId: unitId,
          floorId: floorId,
          siteId: siteId,
          buildingId: buildingId,
        },
      })
      .pipe(
        map((res: any) => {
          if (res?.data && res?.data?.updateUnit) {
            const unit = res.data.updateUnit;
            const units: Units = this.stateManagementService.getUnits(floorId);
            units?.forEach((currentUnit) => {
              if (currentUnit?.unitId === unitId) {
                currentUnit.unitName = currentUnit?.unitPrefix + unitName;
                currentUnit.unitStatus = unitStatusResponse[unitStatus];
              }
            });
            this.stateManagementService.updateUnits(floorId, units);
            return unit;
          } else if (res?.errors?.length > 0) {
            throw new Error(res?.errors[0]?.errorType);
          }
        }),
        catchError((err: any) => {
          switch (err.message) {
            case ERROR_CODES.UNIT_NOT_FOUND:
              throw new NotFoundError(
                this.translate.instant('NO_UNIT_IN_THIS_FLOOR')
              );
            case ERROR_CODES.UNIT_ALREADY_EXISTS:
              throw new DuplicateDataError(
                this.translate.instant('UNIT_NAME_ALREADY_EXIST')
              );
            case ERROR_CODES.FAILED:
              throw new OperationFailedError(
                this.translate.instant('FAILED_TO_UPDATE_UNIT')
              );
            default:
              throw new Error(this.translate.instant('FAILED_TO_UPDATE_UNIT'));
          }
        })
      );
  }

  /**
   * @description getUnit method returns all details related to units
   * @param { any } siteId
   * @param { any } buildingId
   * @param { any } floorId
   *    * @param { any } unitId
   * @returns { Observable<any> }
   */
  getUnit(
    siteId: any,
    buildingId: any,
    floorId: any,
    unitId: any,
    loader?: boolean
  ): Observable<any> {
    return this.http
      .post<any>(
        `${this.url}`,
        {
          query:
            'query ($siteId: String!, $buildingId: String!, $floorId: String!, $unitId: String!) { getUnit (siteId: $siteId, buildingId: $buildingId, floorId: $floorId, unitId: $unitId) {  unitId, unitName, unitStatus , floorName , deviceCount , wingName, buildingName } } ',
          variables: {
            siteId: siteId,
            buildingId: buildingId,
            floorId: floorId,
            unitId: unitId,
          },
        },
        {
          headers: Utility.loaderHeader(loader),
        }
      )
      .pipe(
        map((res: any) => {
          if (res?.data?.getUnit) {
            return res?.data?.getUnit;
          } else if (res.hasOwnProperty('error')) {
            throw new Error(ERROR_CODES.UNIT_NOT_FOUND);
          }
        }),
        catchError((err: any) => {
          switch (err.message) {
            case ERROR_CODES.UNIT_NOT_FOUND:
              throw new OperationFailedError(
                this.translate.instant('UNIT_NOT_FOUND_IN_THIS_FLOOR')
              );
            default:
              throw new Error(
                this.translate.instant('UNIT_NOT_FOUND_IN_THIS_FLOOR')
              );
          }
        })
      );
  }

  /**
   * @description getUnit method returns all details related to units
   * @param { string } siteId
   * @param { string } buildingId
   * @param { string } floorId
   *    * @param { string } accessPointId
   * @returns { Observable<any> }
   */
  getAccessPoints(
    siteId: string,
    buildingId: string,
    floorId: string,
    accessPointId: string | undefined,
    loader?: boolean
  ): Observable<any> {
    const ap = accessPointId?.split(':');
    accessPointId = ap?.pop();
    return this.http
      .post<AccessPoint>(
        `${this.url}`,
        {
          query: `query ($buildingId: String!, $floorId: String!, $siteId: String!, $accessPointId: String!) {
          getAccessPoint(buildingId: $buildingId, floorId: $floorId, siteId: $siteId, accessPointId: $accessPointId) {
            accessPointId
            accessPointName
            accessPointStatus
            floorId
            deviceCount
            floorName
            buildingName
            wingName
          }}`,
          variables: {
            siteId,
            buildingId,
            floorId,
            accessPointId,
          },
        },
        {
          headers: Utility.loaderHeader(loader),
        }
      )
      .pipe(
        map((res: any) => {
          if (res?.data?.getAccessPoint) {
            const floors = this.stateManagementService.getFloors();
            const selectedFloor = floors?.find(
              (currentFloor) => floorId === currentFloor?.floorId
            );
            if (selectedFloor) {
              res.data.getAccessPoint.floorName = selectedFloor?.floorName;
              res.data.getAccessPoint.floorId = selectedFloor?.floorId;
              res.data.getAccessPoint.wingName = selectedFloor?.wingName;
            }
            //updating status with encoding for accesspoint status
            if (
              accessPointStatusResponse.hasOwnProperty(
                res.data.getAccessPoint.accessPointStatus
              )
            ) {
              res.data.getAccessPoint.accessPointStatus =
                this.translate.instant(
                  accessPointStatusResponse[
                    res.data.getAccessPoint.accessPointStatus
                  ]
                );
            }

            return res?.data?.getAccessPoint;
          } else if (res.hasOwnProperty('error')) {
            throw new Error(ERROR_CODES.ACCESS_POINT_NOT_FOUND);
          }
        }),
        catchError((err: any) => {
          switch (err.message) {
            case ERROR_CODES.ACCESS_POINT_NOT_FOUND:
              throw new NotFoundError(
                this.translate.instant('ACCESS_POINT_NOT_FOUND')
              );
            default:
              throw new Error(this.translate.instant('ACCESS_POINT_NOT_FOUND'));
          }
        })
      );
  }

  /**
   * @description ##updateAccessPoint method calls backend API of update AccessPoint
   * @param { string } siteId
   * @param { string } buildingId
   * @param { string } floorId
   * @param { string } accessPointId
   * @param { string } accessPointName
   * @returns { Observable }
   */
  updateAccessPoint(
    siteId: string,
    buildingId: string,
    floorId: string,
    accessPointId: string,
    accessPointName: string
  ): Observable<AccessPoint> {
    return this.http
      .post<AccessPoint>(`${this.url}`, {
        query:
          'mutation ( $accessPointId: String! , $accessPointName: String!) {updateAccessPoint(accessPointId: $accessPointId, accessPointName :$accessPointName) {accessPointId}}',
        variables: {
          accessPointName: accessPointName,
          accessPointId: accessPointId,
        },
      })
      .pipe(
        map((res: any) => {
          if (res?.data && res?.data?.updateAccessPoint) {
            const accessPoint = res.data.updateAccessPoint;
            const units: Units = this.stateManagementService.getUnits(floorId);
            units?.forEach((currentUnit) => {
              if (currentUnit?.accessPointId === accessPointId) {
                currentUnit.accessPointName = accessPointName;
              }
            });
            this.stateManagementService.updateUnits(floorId, units);
            return accessPoint;
          } else if (res?.errors?.length > 0) {
            throw new Error(res?.errors[0]?.errorType);
          }
        }),
        catchError((err: any) => {
          switch (err.message) {
            case ERROR_CODES.ACCESS_POINT_NOT_FOUND:
              throw new NotFoundError(
                this.translate.instant('NO_ACCESSPOINT_IN_THIS_FLOOR')
              );
            case ERROR_CODES.ACCESSPOINT_ALREADY_EXISTS:
              throw new DuplicateDataError(
                this.translate.instant('ACCESSPOINT_NAME_ALREADY_EXIST')
              );
            case ERROR_CODES.FAILED:
              throw new OperationFailedError(
                this.translate.instant('FAILED_TO_UPDATE_ACCESSPOINT')
              );
            default:
              throw new Error(
                this.translate.instant('FAILED_TO_UPDATE_ACCESSPOINT')
              );
          }
        })
      );
  }

  /**
   * @description this method calls API to get all this Devices by Unit Id list
   * @param { string } unitId
   * @returns { devices }
   */
  getDeviceByUnitId(
    unitId: string,
    updateStore: boolean = true,
    loader?: boolean
  ): Observable<IDevice[] | undefined> {
    return this.http
      .post<any>(
        `${this.url}`,
        {
          query: `query ($unitId: String!) { getDevicesByUnit(unitId: $ unitId) { deviceId updatedAt deviceName  activationState batteryAlarm batteryPercentage deviceStatus updatedTime  }}`,
          variables: {
            unitId,
          },
        },
        {
          headers: Utility.loaderHeader(loader),
        }
      )
      .pipe(
        map((res) => {
          try {
            if (res?.data && res?.data?.getDevicesByUnit) {
              const deviceList: IDevice[] = res?.data?.getDevicesByUnit;
              deviceList.forEach((device) => {
                if (
                  device.hasOwnProperty(CONSTANTS.DEVICESTATUS) &&
                  device.deviceStatus !== null &&
                  typeof device.deviceStatus === 'number'
                ) {
                  device.deviceStatus =
                    deviceStatusResponse[device.deviceStatus];
                }
                if (device.hasOwnProperty('activationState')) {
                  device.activationState =
                    activationStateResponse[device.activationState!];
                }
              });
              if (updateStore) {
                this.stateManagementService.updateDeviceList(deviceList);
              }

              return deviceList;
            } else if (res?.errors?.length > 0) {
              let error = res?.errors[0];
              switch (error.errorType) {
                case ERROR_CODES.INTERNAL_SERVER_ERROR:
                  throw new Error(
                    this.translate.instant('FAILED_TO_GET_USERS_IN_THIS_SITE')
                  );
                default:
                  throw new Error(
                    this.translate.instant('FAILED_TO_GET_USERS_IN_THIS_SITE')
                  );
              }
            }
          } catch (error: any) {
            throw new Error(error?.message);
          }
        })
      );
  }

  /**
   * @description this method calls API to get all this Devices by accessPointId Id list
   * @param { string } accessPointId
   * @returns { devices }
   */
  getPartnerDevicesByAccessPoint(
    accessPointId: string,
    loader?: boolean
  ): Observable<IDevice[] | undefined> {
    return this.http
      .post<any>(
        `${this.url}`,
        {
          query: `query ($accessPointId: String!) { getPartnerDevicesByAccessPoint (accessPointId: $accessPointId) { deviceId, deviceName, macAddress, deviceType deviceState }   }`,
          variables: {
            accessPointId,
          },
        },
        {
          headers: Utility.loaderHeader(loader),
        }
      )
      .pipe(
        map((res) => {
          try {
            if (res?.data && res?.data?.getPartnerDevicesByAccessPoint) {
              let deviceList: IDevice[] =
                res?.data?.getPartnerDevicesByAccessPoint;

              // Filter devices with hyphen in deviceId
              deviceList = deviceList.map((listItem: any) => ({
                ...listItem,
                isvisble: !listItem.deviceId.includes('-'),
              }));
              //checking if device list is not empty
              if (deviceList?.length > 0) {
                //finding device state and replacing it with state mapping
                deviceList = deviceList.map((listItem: any) => {
                  if (
                    listItem &&
                    deviceStateResponse.hasOwnProperty(listItem.deviceState)
                  ) {
                    return {
                      ...listItem,
                      deviceState: deviceStateResponse[listItem.deviceState],
                    };
                  }
                  return listItem;
                });
              }

              this.stateManagementService.updatePartnerDeviceList(deviceList);
              return deviceList;
            } else if (res?.errors?.length > 0) {
              let error = res?.errors[0];
              switch (error.errorType) {
                case ERROR_CODES.INTERNAL_SERVER_ERROR:
                  throw new Error(
                    this.translate.instant('FAILED_TO_GET_PARTNER_DEVICES')
                  );
                default:
                  throw new Error(
                    this.translate.instant('FAILED_TO_GET_PARTNER_DEVICES')
                  );
              }
            }
          } catch (error: any) {
            throw new Error(error?.message);
          }
        })
      );
  }

  /**
   * @description this method calls API to get all this Devices by accessPointId Id list
   * @param { string } buildingId
   * @param {boolean}  prepend will be false and replace object 
   * @param { filterCriteria } { eventType: []; dateTo: string; dateFrom: string; searchEntity: searchEntity | string;
  pagination: Pagination;}

   */
  getEventHistoryByAccessPoint(
    accessPointId: string,
    filterCriteria: any,
    prepend = false,
    loader?: boolean
  ): Observable<any | undefined> {
    return this.http
      .post<any>(
        `${this.url}`,
        {
          query: `query($accessPointId: String!, $filterCriteria: FilterCriteria!) 
        { getEventHistoryByAccessPoint (accessPointId: $accessPointId, filterCriteria: $filterCriteria) {events{id, eventCode, eventDescription, user, timestamp, isoTime, deviceName, unitName, buildingName, accessPointName, eventTitle, extraDescription, source, sourceName, isIssue, isAbnormal, isMaster, floorName, wingName}, totalRecords, cursor} }
        `,
          variables: {
            accessPointId,
            filterCriteria,
          },
        },
        {
          headers: Utility.loaderHeader(loader),
        }
      )
      .pipe(
        map((res) => {
          try {
            if (res?.data && res?.data?.getEventHistoryByAccessPoint) {
              const activityList: Activities =
                res?.data?.getEventHistoryByAccessPoint;
              if (prepend) {
                this.stateManagementService.prependAccesPointAcvities(
                  activityList
                );
              } else {
                this.stateManagementService.updateAccessPointAcvities(
                  activityList
                );
              }
              return activityList;
            } else if (res?.errors?.length > 0) {
              this.stateManagementService.updateAccessPointAcvities(
                {} as Activities
              );
              let error = res?.errors[0];
              switch (error.errorType) {
                case ERROR_CODES.INTERNAL_SERVER_ERROR:
                  throw new Error(
                    this.translate.instant('FAILED_TO_GET_PARTNER_DEVICES')
                  );
                default:
                  throw new Error(
                    this.translate.instant('FAILED_TO_GET_PARTNER_DEVICES')
                  );
              }
            }
          } catch (error: any) {
            throw new Error(error?.message);
          }
        })
      );
  }

  /**
   * @description this method calls API to get all this Devices by accessPointId Id list
   * @param { string } buildingId
   * @param {boolean}  prepend will be false and replace object 
   * @param { filterCriteria } { eventType: []; dateTo: string; dateFrom: string; searchEntity: searchEntity | string;
  pagination: Pagination;}

   */
  getEventHistoryByUnit(
    unitId: string,
    filterCriteria: any,
    prepend = false,
    hideLoader?: boolean
  ): Observable<any | undefined> {
    return this.http
      .post<any>(
        `${this.url}`,
        {
          query: `query($unitId: String!, $filterCriteria: FilterCriteria!) { getEventHistoryByUnit(unitId: $unitId, filterCriteria: $filterCriteria) {events{id, eventCode, eventDescription, user, timestamp, isoTime, deviceName, unitName, buildingName, accessPointName, eventTitle, extraDescription, source, sourceName, isIssue, isAbnormal, isMaster, floorName, wingName}, totalRecords, cursor} }`,
          variables: {
            unitId,
            filterCriteria,
          },
        },
        {
          headers: Utility.loaderHeader(hideLoader),
        }
      )
      .pipe(
        map((res) => {
          try {
            if (res?.data && res?.data?.getEventHistoryByUnit) {
              const activityList: Activities = res?.data?.getEventHistoryByUnit;
              if (prepend) {
                this.stateManagementService.prependUnitAcvities(activityList);
              } else {
                this.stateManagementService.updateUnitAcvities(activityList);
              }
              return activityList;
            } else if (res?.errors?.length > 0) {
              this.stateManagementService.updateUnitAcvities({} as Activities);
              let error = res?.errors[0];
              switch (error.errorType) {
                case ERROR_CODES.INTERNAL_SERVER_ERROR:
                  throw new Error(
                    this.translate.instant('FAILED_TO_GET_PARTNER_DEVICES')
                  );
                default:
                  throw new Error(
                    this.translate.instant('FAILED_TO_GET_PARTNER_DEVICES')
                  );
              }
            }
          } catch (error: any) {
            throw new Error(error?.message);
          }
        })
      );
  }
}
